import useSWR, { SWRConfiguration } from 'swr';
import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { RestType } from '@/types/api';
import { AppManagement } from '@/utils/appManagement';
import { store } from '@/redux/store';
import { openModal } from '@/redux/slices/modal';
import { getGoogleAnalyticsClientId } from '@/utils/gaClientId';
import {
  getAccountLockoutFromError,
  isAccountTemporarilyLockedAxiosError,
  isPublicLoginRequest,
  parseLoginApiError,
  shouldSkipAuthRefresh,
} from './authLoginError.util';

const shouldRejectAuthLoginError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  if (shouldSkipAuthRefresh(error.config)) {
    return true;
  }
  if (isAccountTemporarilyLockedAxiosError(error)) {
    return true;
  }
  return isPublicLoginRequest(error.config);
};

/**
 * Determines if an API error should be reported to Sentry.
 * We report server errors (5xx) and unexpected client errors,
 * but not expected errors like auth failures (401, 403) or validation (422).
 */
function shouldReportToSentry(
  status: number | undefined,
  url: string,
): boolean {
  if (!status) return true; // Network errors without status should be reported

  // Always report server errors (5xx)
  if (status >= 500) return true;

  // Don't report expected client errors
  const expectedClientErrors = [401, 403, 404, 422];
  if (expectedClientErrors.includes(status)) return false;

  // Report other client errors (400, 405, etc.) as they might indicate bugs
  if (status >= 400) return true;

  return false;
}

/**
 * Reports an API error to Sentry with rich context
 */
function reportApiErrorToSentry(
  error: unknown,
  context: {
    url?: string;
    method?: string;
    status?: number;
    statusText?: string;
    responseData?: unknown;
  },
): void {
  const { url, method, status, statusText, responseData } = context;

  if (!shouldReportToSentry(status, url || '')) return;

  Sentry.withScope((scope) => {
    scope.setTag('error.type', 'api_error');
    scope.setTag('api.method', method?.toUpperCase() || 'UNKNOWN');
    scope.setTag('api.status', status?.toString() || 'NETWORK_ERROR');

    scope.setContext('API Request', {
      url,
      method: method?.toUpperCase(),
      status,
      statusText,
    });

    if (responseData) {
      scope.setContext('API Response', {
        data:
          typeof responseData === 'object'
            ? JSON.stringify(responseData).slice(0, 1000) // Limit size
            : responseData,
      });
    }

    const errorMessage = status
      ? `API Error: ${method?.toUpperCase()} ${url} - ${status} ${statusText}`
      : `API Network Error: ${method?.toUpperCase()} ${url}`;

    if (error instanceof Error) {
      error.message = errorMessage;
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(errorMessage, 'error');
    }
  });
}

// Configuración base para Axios
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.example.com',
  timeout: 15000,
});

// Funciones para manejo de localStorage
function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

function setAccessToken(token: string): void {
  localStorage.setItem('access_token', token);
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token');
}

function setRefreshToken(token: string): void {
  localStorage.setItem('refresh_token', token);
}

function clearTokens(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

// Interceptor para manejar solicitudes
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }

  const gaClientId = getGoogleAnalyticsClientId();
  if (gaClientId) {
    config.headers = {
      ...config.headers,
      gs_ga_client_id: gaClientId,
    };
  }
  return config;
});

// Interceptor para manejar respuestas
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Si el backend regresa un nuevo accessToken en login o sign up o refresh, guárdalo en localStorage
    const newAccessToken = response.data?.accessToken;
    if (newAccessToken) {
      setAccessToken(newAccessToken);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (shouldRejectAuthLoginError(error)) {
      return Promise.reject(error);
    }

    // Report error to Sentry (before handling refresh logic)
    if (axios.isAxiosError(error)) {
      reportApiErrorToSentry(error, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
      });
    }

    if (error.response?.status === 401 && !originalRequest.retry) {
      originalRequest.retry = true;
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const { data } = await apiClient.post('/auth/refresh-token', {
            refresh_token: refreshToken,
          });
          setAccessToken(data.access_token);
          setRefreshToken(data.refresh_token);
          originalRequest.headers.authorization = `Bearer ${data.access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          console.error('Error al refrescar el token:', refreshError);
          clearTokens();
          if (!isPublicLoginRequest(originalRequest)) {
            store.dispatch(openModal());
          }
          return Promise.reject(
            isPublicLoginRequest(originalRequest) ? error : refreshError,
          );
        }
      } else {
        clearTokens();
        if (!isPublicLoginRequest(originalRequest)) {
          store.dispatch(openModal());
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

// Función para requests que no sean GET
async function apiRequest<T, U = unknown>(
  method: RestType,
  endpoint: string,
  data?: U,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await apiClient.request<T>({
      method,
      url: endpoint,
      data,
      ...config,
    });
    return response.data;
  } catch (error: unknown) {
    // Note: API errors are already reported to Sentry in the interceptor
    const loginApiError = parseLoginApiError(error);
    if (loginApiError && getAccountLockoutFromError(error)) {
      throw loginApiError;
    }
    if (axios.isAxiosError(error) && error.response) {
      throw error.response.data;
    }

    // For non-Axios errors (network issues, etc.), report to Sentry
    reportApiErrorToSentry(error, {
      url: endpoint,
      method,
    });

    throw new Error('Error desconocido en la solicitud API');
  }
}

// Fetcher para SWR
// Note: API errors are automatically reported to Sentry via the response interceptor
const fetcher = async (url: string, config?: AxiosRequestConfig) => {
  const response = await apiClient.get(url, config);
  return response.data;
};

// Métodos específicos
export const apiBase = {
  get: (endpoint: string, config?: AxiosRequestConfig) =>
    fetcher(endpoint, config),
  post: <T, J>(
    endpoint: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<J> => apiRequest(RestType.POST, endpoint, data, config),
  put: <T>(endpoint: string, data: T, config?: AxiosRequestConfig) =>
    apiRequest(RestType.PUT, endpoint, data, config),
  patch: <T>(endpoint: string, data: T, config?: AxiosRequestConfig) =>
    apiRequest(RestType.PATCH, endpoint, data, config),
  delete: (endpoint: string, config?: AxiosRequestConfig) =>
    apiRequest(RestType.DELETE, endpoint, undefined, config),
};

// Gets de url
export const baseUrl = () => AppManagement.getBaseUrl();
export const moduleUrl = () => AppManagement.getModuleUrl();

// Hook para manejo de requests con SWR
export function useRequest<T>(endpoint: string, config?: SWRConfiguration) {
  const { data, error, mutate } = useSWR<T>(endpoint, fetcher, config);

  return {
    data,
    error,
    isLoading: !error && !data,
    mutate,
  };
}
